/**
 * The audio graph and its lifecycle. Knows about voices, buses and mute;
 * knows nothing about puzzles — game-facing events live in index.ts.
 *
 * Everything expensive is built exactly once:
 *
 *   voice[n].gain → voice[n].filter ┐
 *                                   ├→ bus ┬→ dry ──────────────→ master → limiter → out
 *                                   ┘      ├→ reverbSend → convolver → wet ↗
 *                                          └→ delaySend → delay ⇄ feedback → wet ↗
 *
 * Playing a note allocates a single AudioBufferSourceNode — the one node the
 * Web Audio API will not let you reuse (sources are one-shot by design). The
 * gain, the filter, the convolver, the delay line and the limiter are all
 * pre-built and re-targeted per note, so a move costs one small allocation
 * and a handful of param writes rather than a graph build.
 *
 * Notes are never cut off by the note that follows them: each voice rings out
 * its full two-to-three second tail while later notes start on other voices.
 * With 24 voices, stealing only happens 24 notes deep — by then the stolen
 * tail is inaudible, and it is faded rather than stopped so there is no click.
 */

import type { SoundPack } from "./pack.ts";
import { SCALE_LENGTH } from "./scale.ts";

const VOICE_COUNT = 24;
const MUTE_STORAGE_KEY = "pathpuzzle.muted";

/** Master trim. Leaves headroom for a dozen overlapping tails. */
const MASTER_GAIN = 0.75;

/** Fade applied to master on mute — long enough to avoid a click, short enough to feel instant. */
const MUTE_FADE_SECONDS = 0.04;

export interface PlayOptions {
  /** 0..1, pre-master. */
  velocity?: number;
  /** 0..1 → how far the voice's lowpass opens. Low reads as muted/darker. */
  brightness?: number;
  /** Seconds from now. Used to schedule the win arpeggio precisely. */
  when?: number;
  /** If set, fade the note out this many seconds in, instead of letting it ring. */
  damp?: number;
}

interface Voice {
  gain: GainNode;
  filter: BiquadFilterNode;
  source: AudioBufferSourceNode | null;
}

type ContextCtor = new (options?: AudioContextOptions) => AudioContext;

function createContext(): AudioContext | null {
  const Ctor =
    (globalThis as unknown as { AudioContext?: ContextCtor; webkitAudioContext?: ContextCtor }).AudioContext ??
    (globalThis as unknown as { webkitAudioContext?: ContextCtor }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor({ latencyHint: "interactive" });
  } catch {
    return null;
  }
}

function readStoredMute(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === "1";
  } catch {
    // Safari private browsing throws on localStorage access.
    return false;
  }
}

function writeStoredMute(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    /* not persisting is survivable; failing to play audio is not */
  }
}

export class AudioEngine {
  private readonly ctx: AudioContext | null;
  private readonly voices: Voice[] = [];
  private nextVoice = 0;

  private master!: GainNode;
  private wet!: GainNode;
  private wetBase = 1;
  private buffers: AudioBuffer[] | null = null;
  private packGain = 1;

  private muted = readStoredMute();
  private kicked = false;

  constructor(pack: SoundPack, frequencies: readonly number[]) {
    this.ctx = createContext();
    if (!this.ctx) return;

    this.buildGraph(this.ctx, pack);
    this.packGain = pack.mix.gain;

    // Render/fetch ahead of the first gesture so the very first tap has a
    // note ready. The context is created suspended (browsers require a
    // gesture to start it), but rendering does not need it to be running.
    void pack
      .load(this.ctx.sampleRate, frequencies)
      .then((buffers) => {
        this.buffers = buffers;
      })
      .catch((err) => {
        console.warn("sound pack failed to load; running silent", err);
      });
  }

  /** True once the pack is loaded and notes will actually sound. */
  get ready(): boolean {
    return this.buffers !== null;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    writeStoredMute(muted);
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(muted ? 0 : MASTER_GAIN, now + MUTE_FADE_SECONDS);
  }

  /**
   * Call from a pointerdown handler. Browsers refuse to start an
   * AudioContext outside a user gesture, and iOS additionally wants to see
   * something actually played inside that gesture before it opens the output
   * — hence the one-sample silent buffer.
   *
   * Safe and cheap to call on every pointerdown, which is deliberate: iOS
   * suspends the context when the app is backgrounded, and the next tap is
   * exactly when we want it back.
   */
  unlock(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state !== "running") void ctx.resume().catch(() => {});
    if (this.kicked) return;
    this.kicked = true;
    try {
      const silent = ctx.createBufferSource();
      silent.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      silent.connect(ctx.destination);
      silent.start(0);
    } catch {
      /* the resume above is the part that matters */
    }
  }

  /** Play scale note `index` (wrapped into range). No-op while muted or unloaded. */
  play(index: number, options: PlayOptions = {}): void {
    const ctx = this.ctx;
    if (!ctx || !this.buffers || this.muted) return;

    const buffer = this.buffers[((index % SCALE_LENGTH) + SCALE_LENGTH) % SCALE_LENGTH];
    if (!buffer) return;

    const velocity = (options.velocity ?? 0.8) * this.packGain;
    const brightness = options.brightness ?? 1;
    const at = ctx.currentTime + (options.when ?? 0);

    const voice = this.voices[this.nextVoice];
    this.nextVoice = (this.nextVoice + 1) % VOICE_COUNT;

    // Retire whatever this voice was still ringing. The 5ms ramp below moves
    // its level rather than cutting it, and the stop lands well after, so
    // neither transition produces a discontinuity.
    if (voice.source) {
      try {
        voice.source.stop(at + 0.05);
      } catch {
        /* already stopped */
      }
    }

    const cutoff = Math.min(18000, Math.max(400, 900 + 5200 * brightness));
    voice.filter.frequency.cancelScheduledValues(at);
    voice.filter.frequency.setValueAtTime(cutoff, at);

    const gain = voice.gain.gain;
    gain.cancelScheduledValues(at);
    gain.setValueAtTime(gain.value, at);
    gain.linearRampToValueAtTime(velocity, at + 0.005);
    if (options.damp !== undefined) {
      gain.setValueAtTime(velocity, at + options.damp);
      gain.exponentialRampToValueAtTime(0.0001, at + options.damp + 0.3);
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(voice.gain);
    source.start(at);
    source.onended = () => {
      source.disconnect();
      if (voice.source === source) voice.source = null;
    };
    voice.source = source;
  }

  /** Temporarily open up the reverb — used to let a win bloom. */
  swellReverb(amount: number, seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || this.muted) return;
    const now = ctx.currentTime;
    const base = this.wetBase;
    this.wet.gain.cancelScheduledValues(now);
    this.wet.gain.setValueAtTime(this.wet.gain.value, now);
    this.wet.gain.linearRampToValueAtTime(base * amount, now + 0.08);
    this.wet.gain.linearRampToValueAtTime(base, now + seconds);
  }

  private buildGraph(ctx: AudioContext, pack: SoundPack): void {
    // A limiter on the master is the safety net for overlap: two dozen
    // ringing tails plus reverb can otherwise clip on a fast drag.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -8;
    limiter.knee.value = 6;
    limiter.ratio.value = 6;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    limiter.connect(ctx.destination);

    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
    this.master.connect(limiter);

    const bus = ctx.createGain();
    const dry = ctx.createGain();
    dry.gain.value = 0.85;
    bus.connect(dry).connect(this.master);

    this.wet = ctx.createGain();
    this.wet.gain.value = this.wetBase;
    this.wet.connect(this.master);

    const convolver = ctx.createConvolver();
    convolver.buffer = renderImpulseResponse(ctx, pack.mix.reverbSeconds ?? 2);
    const reverbSend = ctx.createGain();
    reverbSend.gain.value = pack.mix.reverb;
    bus.connect(reverbSend).connect(convolver).connect(this.wet);

    // A short feedback delay under the reverb: adds movement to a held tail
    // without the wash a longer reverb would need. Pentatonic means echoes
    // of one note never clash with the next.
    //
    // Kept well under the interval between two tiles at speed. At 260ms the
    // echo of the previous tile landed on top of the next one, and a board
    // played quickly turned into a wash arriving behind the finger -- which
    // reads as lag even though every note starts on time.
    const delay = ctx.createDelay(1);
    delay.delayTime.value = 0.13;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.16;
    const damp = ctx.createBiquadFilter();
    damp.type = "lowpass";
    damp.frequency.value = 2200;
    delay.connect(damp).connect(feedback).connect(delay);
    const delaySend = ctx.createGain();
    delaySend.gain.value = pack.mix.delay;
    bus.connect(delaySend).connect(delay);
    delay.connect(this.wet);

    for (let i = 0; i < VOICE_COUNT; i++) {
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 6000;
      filter.Q.value = 0.5;
      gain.connect(filter).connect(bus);
      this.voices.push({ gain, filter, source: null });
    }
  }
}

/**
 * Exponentially decaying noise — a plain, cheap room. Built once at startup;
 * a convolver with a generated IR costs nothing extra per note, which is why
 * this beats scheduling a delay network per voice.
 */
function renderImpulseResponse(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.ceil(seconds * rate);
  const preDelay = Math.floor(0.012 * rate);
  const ir = ctx.createBuffer(2, length, rate);
  for (let channel = 0; channel < 2; channel++) {
    const data = ir.getChannelData(channel);
    for (let i = preDelay; i < length; i++) {
      const t = (i - preDelay) / (length - preDelay);
      data[i] = (Math.random() * 2 - 1) * (1 - t) ** 2.6;
    }
  }
  return ir;
}
